// status-immunity-delta.ts — Janus C1 (2026-09-28, CMP2): the STATUS share of finalize_creature_arr's status-immunity fold.
// layer_on_status_effects appends a held status's `immune_status_effect` values onto the host ROOT; finalize seeds
// computed.creature.defenses.immune_status from that root list FIRST (`$_self_status_source` — the host itself), before permanent
// statuses and feats / passives, first-wins per status id (later sources fold into all_sources). The same exactness rules as the
// damage-type delta (creature-defense-delta.ts applyStatusDamageDefenseDelta): the status must be the sole provider of each status id,
// and no host-attributed entry may already sit in the store (their relative order would depend on the host's own authored order):
//   apply  → the new host entries go FIRST, in fragment order;
//   remove → each entry must be host-attributed with no extra sources, and leaves.
// Anything else refuses by name (the rebuild serves). contesting_roll_*_vs_status_effect needs no delta: finalize strips those root
// slots and never stores a status's share (readers walk the held statuses) — the compile output does not move.

import { foldDefenseContributors } from './creature-defense-delta';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => typeof v === 'string' ? v.toUpperCase() : '';
const uuidOf = (v: unknown): string => upper(isObj(v) ? v.item_layer_effect ?? v.item_id ?? (isObj(v.items) ? (v.items as Row).item_id : '') : typeof v === 'string' ? v.split('|', 1)[0] : '');

export interface StatusImmunityInput {
    defenses: Row | undefined;
    host: { id: string; name: string; kind: string | null };
    applied: boolean;
    /** the status definition's own immune_status_effect fragments (authored order) */
    fragments: unknown[];
    /** status ids the host's own authored definition grants (the stored row's root slot is stripped — read the definition: a non-PC's
     *  creature item; a PC authors none), and every OTHER target status held after the mutation */
    hostOwn: string[];
    otherHeld: string[];
}
export type StatusImmunityResult = { ok: true; defenses: Row | undefined } | { ok: false; reason: string };

export function applyStatusImmunityDelta(input: StatusImmunityInput): StatusImmunityResult {
    const defenses: Row = isObj(input.defenses) ? JSON.parse(JSON.stringify(input.defenses)) as Row : {};
    const hostId = upper(input.host.id);
    const seen = new Set<string>();
    const ids = input.fragments.map(uuidOf).filter(t => t !== '' && !seen.has(t) && (seen.add(t), true));
    if (!ids.length) return { ok: true, defenses: Object.keys(defenses).length ? defenses : undefined };
    for (const t of ids) {
        if (input.hostOwn.map(upper).includes(t) || input.otherHeld.map(upper).includes(t)) return { ok: false, reason: 'unported:status-immunity:shared' };
    }
    const store = Array.isArray(defenses.immune_status) ? (defenses.immune_status as unknown[]).slice() : [];
    const idOf = (e: unknown): string => isObj(e) ? upper(e.item_layer_effect) : upper(e);
    if (input.applied) {
        if (store.some(e => !isObj(e))) return { ok: false, reason: 'unported:status-immunity:shape' };
        // finalize's seed walks layer_entries(host, 'immune_status_effect'): the host's OWN authored immunities, then each held status's
        // appended share, then the carrier walk (ancestry / class / type — host-attributed as well). Measured on the T4 PC (status oracle,
        // Invulnerable): the status's entry lands BEFORE a carrier-derived host entry. So the new entries go right after the leading
        // own-authored ones; another held status's share would make the order depend on status fetch order → refuse.
        if (input.otherHeld.length) return { ok: false, reason: 'unported:status-immunity:multi-status' };
        const own = new Set(input.hostOwn.map(upper));
        let lead = 0;
        while (lead < store.length && isObj(store[lead]) && upper((store[lead] as Row).source_item_id) === hostId && own.has(idOf(store[lead]))) lead++;
        if (store.slice(0, lead).some(e => ids.includes(idOf(e)))) return { ok: false, reason: 'unported:status-immunity:dup-own' };
        // a status id another provider already grants (the `dup` family): first-wins — the host entry is created ahead and the earlier
        // provider (then its contributors) folds into all_sources; its old position leaves
        let rest = store.slice(lead);
        const fresh = ids.map(t => {
            const entry: Row = { item_layer_effect: t };
            if (input.host.id) entry.source_item_id = input.host.id;
            if (input.host.name) entry.source_name = input.host.name;
            if (input.host.kind) entry.source_kind = input.host.kind;
            const i = rest.findIndex(e => idOf(e) === t);
            if (i >= 0) { const folded = foldDefenseContributors(rest[i] as Row, hostId); if (folded.length) entry.all_sources = folded; rest = rest.filter((_, k) => k !== i); }
            return entry;
        });
        defenses.immune_status = [...store.slice(0, lead), ...fresh, ...rest];
    } else {
        let next = store;
        for (const t of ids) {
            const i = next.findIndex(e => idOf(e) === t);
            if (i < 0) return { ok: false, reason: 'unported:status-immunity:missing' };
            const e = next[i];
            if (!isObj(e) || upper(e.source_item_id) !== hostId || (Array.isArray(e.all_sources) && e.all_sources.length)) return { ok: false, reason: 'unported:status-immunity:shared' };
            next = next.filter((_, k) => k !== i);
        }
        if (next.length) defenses.immune_status = next; else delete defenses.immune_status;
    }
    return { ok: true, defenses: Object.keys(defenses).length ? defenses : undefined };
}
